import { useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import Topbar from '../components/dashboard/Topbar';
import { C2GoTag } from '../components/c2';
import { api, getToken, type Link as LinkRecord, type User } from '../lib/api';

type Period = 7 | 30 | 90;

interface AnalyticsData {
  totalClicks: number;
  dailyClicks: Array<{ date: string; count: number }>;
  topLinks: Array<{ slug: string; url: string; description: string | null; clicks: number }>;
}

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  primary?: boolean;
  warn?: boolean;
}

function StatCard({ label, value, sub, primary, warn }: StatCardProps) {
  return (
    <div
      className="flex flex-col gap-1.5"
      style={{
        background: primary ? 'var(--accent-tint)' : 'var(--color-surface)',
        border: `1px solid ${primary ? 'var(--accent-soft)' : 'var(--color-hairline)'}`,
        borderRadius: 14,
        padding: '16px 18px 14px',
      }}
    >
      <div className="text-xs font-medium text-ink-soft">{label}</div>
      <div
        className="font-display font-extrabold text-ink leading-none"
        style={{ fontSize: 30, letterSpacing: '-0.02em' }}
      >
        {value}
      </div>
      {sub && (
        <div
          className="text-xs"
          style={{
            color: warn ? 'var(--color-peach-deep)' : 'var(--color-ink-muted)',
            fontWeight: warn ? 600 : 500,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  sub?: string;
  children: React.ReactNode;
  compact?: boolean;
}

function Section({ title, sub, children, compact }: SectionProps) {
  return (
    <div
      className="bg-surface overflow-hidden"
      style={{ border: '1px solid var(--color-hairline)', borderRadius: 14 }}
    >
      <div
        className="flex items-baseline justify-between"
        style={{
          padding: compact ? '14px 18px' : '16px 20px',
          borderBottom: '1px solid var(--color-hairline)',
          gap: 12,
        }}
      >
        <div>
          <h3
            className="font-display font-bold text-ink m-0"
            style={{ fontSize: 16, letterSpacing: '-0.01em' }}
          >
            {title}
          </h3>
          {sub && <div className="text-xs text-ink-muted mt-0.5">{sub}</div>}
        </div>
      </div>
      <div style={{ padding: compact ? 0 : 20 }}>{children}</div>
    </div>
  );
}

interface BigSparkProps {
  data: number[];
  /** ISO date strings parallel to `data`. */
  dates: string[];
}

function BigSpark({ data, dates }: BigSparkProps) {
  const [hover, setHover] = useState<number | null>(null);
  if (data.length === 0) {
    return (
      <div className="text-center text-ink-muted text-sm" style={{ padding: '40px 20px' }}>
        No clicks yet for this period.
      </div>
    );
  }
  const max = Math.max(...data, 1);
  const W = 1100;
  const H = 180;
  const PAD_X = 12;
  const PAD_Y = 24;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;
  const barGap = 4;
  const barW = (innerW - barGap * (data.length - 1)) / data.length;

  // X-axis ticks (only show a few — first, middle-ish, last)
  const tickIndexes = data.length <= 5
    ? data.map((_, i) => i)
    : [0, Math.floor(data.length * 0.25), Math.floor(data.length * 0.5), Math.floor(data.length * 0.75), data.length - 1];

  return (
    <svg viewBox={`0 0 ${W} ${H + 24}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {data.map((v, i) => {
        const h = (v / max) * innerH;
        const x = PAD_X + i * (barW + barGap);
        const y = PAD_Y + (innerH - h);
        const isHover = hover === i;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={Math.max(h, 1)}
              rx={Math.min(3, barW / 2)}
              fill={isHover ? 'var(--accent-deep)' : 'var(--accent-solid)'}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: 'pointer', transition: 'fill 120ms ease' }}
            />
            {isHover && (
              <>
                <rect x={x + barW / 2 - 50} y={y - 30} width={100} height={22} rx={6} fill="var(--color-ink)" />
                <text x={x + barW / 2} y={y - 15} textAnchor="middle" fontSize={11} fontWeight={600} fill="#fff">
                  {v} {v === 1 ? 'click' : 'clicks'}
                </text>
              </>
            )}
          </g>
        );
      })}
      {tickIndexes.map(i => {
        const x = PAD_X + i * (barW + barGap) + barW / 2;
        const date = dates[i];
        const label = date ? new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
        return (
          <text key={i} x={x} y={H + 16} textAnchor="middle" fontSize={11} fontWeight={500} fill="var(--color-ink-muted)">
            {label}
          </text>
        );
      })}
    </svg>
  );
}

export default function AnalyticsPage() {
  const [user, setUser] = useState<User | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [allLinks, setAllLinks] = useState<LinkRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>(30);

  useEffect(() => {
    if (!getToken()) {
      window.location.href = '/login';
    }
  }, []);

  const refresh = async (p: Period) => {
    setLoading(true);
    try {
      const [me, a, links] = await Promise.all([api.me(), api.analytics(p), api.listLinks()]);
      setUser(me);
      setAnalytics(a);
      setAllLinks(links);
      setLoadError(null);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 403) {
        window.location.href = '/login';
        return;
      }
      setLoadError((err as Error).message || 'Could not load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh(period);
  }, [period]);

  const dailyData = analytics?.dailyClicks ?? [];
  const dailyCounts = dailyData.map(d => d.count);
  const dailyDates = dailyData.map(d => d.date);
  const periodTotal = dailyCounts.reduce((s, v) => s + v, 0);
  const maxTopClicks = analytics?.topLinks?.[0]?.clicks ?? 1;

  const unusedLinks = useMemo(
    () => allLinks.filter(l => l.alias_of == null && l.click_count === 0),
    [allLinks],
  );
  const activeCount = allLinks.filter(l => l.alias_of == null && l.click_count > 0).length;
  const totalLinks = allLinks.filter(l => l.alias_of == null).length;

  return (
    <div className="flex min-h-screen bg-bg font-body">
      <Sidebar user={user} />

      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar
          crumbs={[user?.email?.split('@')[1]?.split('.')[0] ?? 'Workspace', 'Analytics']}
          rightSlot={
            <select
              value={period}
              onChange={e => setPeriod(Number(e.target.value) as Period)}
              className="cursor-pointer outline-none"
              style={{
                padding: '7px 10px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-hairline)',
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--color-ink)',
                fontFamily: 'inherit',
              }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          }
        />

        <div style={{ padding: '28px 32px 80px', maxWidth: 1280, width: '100%', margin: '0 auto' }} className="w-full">
          {loading && !analytics ? (
            <div className="text-center text-ink-soft" style={{ padding: '60px 20px' }}>Loading…</div>
          ) : loadError ? (
            <div className="text-center text-ink-soft" style={{ padding: '60px 20px' }}>{loadError}</div>
          ) : (
            <>
              <div className="mb-6">
                <h1
                  className="font-display font-extrabold text-ink m-0"
                  style={{ fontSize: 32, letterSpacing: '-0.02em' }}
                >
                  Workspace activity
                </h1>
                <p className="text-sm text-ink-soft mt-2 m-0 leading-[1.55]" style={{ maxWidth: 640 }}>
                  All links across your workspace. Click data is aggregated — we don't log who clicked what beyond the redirect timestamp.
                </p>
              </div>

              <div className="grid mb-7" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                <StatCard label={`Clicks · last ${period}d`} value={periodTotal.toLocaleString()} primary />
                <StatCard label="Active links" value={String(activeCount)} sub={`of ${totalLinks} total`} />
                <StatCard
                  label="All-time clicks"
                  value={(analytics?.totalClicks ?? 0).toLocaleString()}
                  sub="across all links"
                />
                <StatCard
                  label="Unused links"
                  value={String(unusedLinks.length)}
                  sub="0 clicks ever"
                  warn={unusedLinks.length > 0}
                />
              </div>

              <Section
                title="Clicks over time"
                sub={`Daily clicks for the last ${period} days · totals: ${periodTotal.toLocaleString()}`}
              >
                <BigSpark data={dailyCounts} dates={dailyDates} />
              </Section>

              <div className="mt-7">
                <Section title="Top links" sub={`Sorted by clicks · last ${period}d`} compact>
                  {(analytics?.topLinks ?? []).length === 0 ? (
                    <div className="text-center text-ink-muted text-sm" style={{ padding: '32px 20px' }}>
                      No clicks yet — share a link with your team!
                    </div>
                  ) : (
                    <div>
                      <div
                        className="grid items-center bg-bg-alt"
                        style={{
                          gridTemplateColumns: '1fr 90px 160px',
                          padding: '10px 20px',
                          borderBottom: '1px solid var(--color-hairline)',
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'var(--color-ink-muted)',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        <div>Link</div>
                        <div className="text-right">Clicks</div>
                        <div>Trend</div>
                      </div>
                      {analytics!.topLinks.map((r, i) => {
                        const barPct = (r.clicks / maxTopClicks) * 100;
                        return (
                          <div
                            key={r.slug}
                            className="grid items-center transition-colors duration-150"
                            style={{
                              gridTemplateColumns: '1fr 90px 160px',
                              padding: '12px 20px',
                              borderBottom: i === analytics!.topLinks.length - 1 ? 'none' : '1px solid var(--color-hairline)',
                              fontSize: 13,
                            }}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <C2GoTag slug={r.slug} size="sm" />
                              {r.description && (
                                <span className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap">
                                  {r.description}
                                </span>
                              )}
                            </div>
                            <div className="text-right font-mono font-bold text-ink text-[13px]">
                              {r.clicks.toLocaleString()}
                            </div>
                            <div className="flex items-center">
                              <div
                                className="flex-1 overflow-hidden"
                                style={{ height: 6, background: 'var(--color-bg-alt)', borderRadius: 999 }}
                              >
                                <div
                                  style={{
                                    width: `${barPct}%`,
                                    height: '100%',
                                    background: 'var(--accent-solid)',
                                    borderRadius: 999,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Section>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
